import { Link } from 'react-router-dom';
import { Bot, MessageSquare, FileText, Phone } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/use-auth';

interface DashboardSection {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
}

const allSections: DashboardSection[] = [
  {
    title: 'LLM Configuration',
    description: 'Manage AI provider settings, models, and prompts',
    href: '/llm-config',
    icon: <Bot className="h-6 w-6 text-primary" />,
  },
  {
    title: 'WhatsApp Configuration',
    description: 'Configure WhatsApp Business API credentials and endpoints',
    href: '/whatsapp-config',
    icon: <Phone className="h-6 w-6 text-primary" />,
  },
  {
    title: 'Documents',
    description: 'Upload and manage documents for AI processing',
    href: '/documents',
    icon: <FileText className="h-6 w-6 text-primary" />,
  },
  {
    title: 'Messages',
    description: 'View WhatsApp message history and conversations',
    href: '/messages',
    icon: <MessageSquare className="h-6 w-6 text-primary" />,
  },
];

const tenantSections: DashboardSection[] = [
  {
    title: 'LLM Configuration',
    description: 'View your AI provider settings and model configuration',
    href: '/llm-config',
    icon: <Bot className="h-6 w-6 text-primary" />,
  },
  {
    title: 'Documents',
    description: 'Upload and manage your documents for AI processing',
    href: '/documents',
    icon: <FileText className="h-6 w-6 text-primary" />,
  },
  {
    title: 'Messages',
    description: 'View your WhatsApp message history',
    href: '/messages',
    icon: <MessageSquare className="h-6 w-6 text-primary" />,
  },
];

export default function DashboardPage() {
  const { isAdmin, displayName } = useAuth();

  const sections = isAdmin ? allSections : tenantSections;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome back{displayName ? `, ${displayName}` : ''}. {isAdmin ? 'Manage all services from here.' : 'Access your services below.'}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((section) => (
          <Link key={section.href} to={section.href} className="block">
            <Card className="h-full transition-colors hover:border-primary/50 hover:bg-muted/50">
              <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
                {section.icon}
                <CardTitle className="text-lg">{section.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>{section.description}</CardDescription>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
